<script lang="ts" setup>
import {ref, computed, watch} from 'vue'
import {storeToRefs} from 'pinia'
import {useAppStore} from '@/stores/app'
import {
  useTaskCreateForm,
  durationOptions,
  energyOptions,
  reminderPresets,
  folderOptions,
} from '@/composables/useTaskCreateForm'
import {useToast} from '@/composables/useToast'
import TagsMultiSelect from './TagsMultiSelect.vue'
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Separator} from '@/components/ui/separator'
import {Badge} from '@/components/ui/badge'
import {Switch} from '@/components/ui/switch'
import {CalendarDateTime, type DateValue} from '@internationalized/date'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Calendar} from '@/components/ui/calendar'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible'
import {CalendarDays, ChevronDown, X} from 'lucide-vue-next'

const props = defineProps<{
  open: boolean
  folder?: string
  projectId?: string
  focus?: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const appStore = useAppStore()
const {projects, tags, contacts} = storeToRefs(appStore)
const toast = useToast()

const formOptions = computed(() => ({
  defaultFolder: props.folder,
  defaultProjectId: props.projectId,
  defaultFocused: props.focus,
}))

const {
  title,
  notes,
  folder,
  dueDate,
  duration,
  energy,
  delegatedTo,
  projectId,
  selectedTags,
  reminders,
  autoComplete,
  canConfigureAutoComplete,
  recurringSchedule,
  recurringCondition,
  recurringStartAt,
  recurringEndAt,
  isAddingNewContact,
  newContactName,
  isSaving,
  canSave,
  resetForm,
  addReminder,
  removeReminder,
  handleNewContact,
  saveTask,
} = useTaskCreateForm(formOptions)

const discardDialogOpen = ref(false)
const dueDatePopoverOpen = ref(false)
const recurringStartPopoverOpen = ref(false)
const recurringEndPopoverOpen = ref(false)

const createFolderOptions = folderOptions.filter((f) => f !== 'trash')

const dueTime = computed({
  get() {
    if (!dueDate.value || !('hour' in dueDate.value)) return '09:00'
    return `${String(dueDate.value.hour).padStart(2, '0')}:${String(dueDate.value.minute).padStart(2, '0')}`
  },
  set(val: string) {
    if (!dueDate.value) return
    const [h, m] = val.split(':').map(Number)
    dueDate.value = new CalendarDateTime(dueDate.value.year, dueDate.value.month, dueDate.value.day, h ?? 9, m ?? 0)
  },
})

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      resetForm()
    }
  }
)

function handleClose() {
  if (title.value.trim()) {
    discardDialogOpen.value = true
  } else {
    resetForm()
    emit('update:open', false)
  }
}

function discardAndClose() {
  discardDialogOpen.value = false
  resetForm()
  emit('update:open', false)
}

async function handleSave() {
  const result = await saveTask()
  if (result.success) {
    toast.success('Task created')
    resetForm()
    emit('update:open', false)
  } else {
    toast.error(result.error || 'Failed to create task')
  }
}

function handleReminderSelect(value: string | number | bigint | Record<string, any> | null) {
  if (typeof value === 'string' && value) {
    addReminder(value)
  }
}

function selectDueDate(value: DateValue | undefined) {
  if (value) {
    const [h, m] = dueTime.value.split(':').map(Number)
    dueDate.value = new CalendarDateTime(value.year, value.month, value.day, h ?? 9, m ?? 0)
  } else {
    dueDate.value = undefined
  }
  dueDatePopoverOpen.value = false
}

function selectRecurringStartAt(value: DateValue | undefined) {
  recurringStartAt.value = value
  recurringStartPopoverOpen.value = false
}

function selectRecurringEndAt(value: DateValue | undefined) {
  recurringEndAt.value = value
  recurringEndPopoverOpen.value = false
}

function formatDate(date: DateValue): string {
  return `${date.day.toString().padStart(2, '0')}.${date.month.toString().padStart(2, '0')}.${date.year}`
}

function handleDelegatedToChange(value: string | number | bigint | Record<string, any> | null) {
  if (typeof value !== 'string') return
  if (value === '__new__') {
    isAddingNewContact.value = true
    delegatedTo.value = ''
  } else {
    delegatedTo.value = value === '__none__' ? '' : value
  }
}

function cancelNewContact() {
  isAddingNewContact.value = false
  newContactName.value = ''
}

function getReminderLabel(value: string): string {
  const preset = reminderPresets.find((p) => p.value === value)
  return preset ? preset.label : value
}
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogScrollContent class="max-w-lg">
      <DialogHeader>
        <DialogTitle>{{ $t('actions.new_task') }}</DialogTitle>
        <DialogDescription class="sr-only">{{ $t('actions.new_task') }}</DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-2">
        <!-- Core: Title -->
        <div class="grid gap-2">
          <Label for="create-title">{{ $t('fields.title') }}</Label>
          <Input id="create-title" v-model="title" :placeholder="$t('fields.title')" />
        </div>

        <!-- Core: Notes -->
        <div class="grid gap-2">
          <Label for="create-notes">{{ $t('fields.notes') }}</Label>
          <Textarea id="create-notes" v-model="notes" rows="3" :placeholder="$t('fields.notes')" />
        </div>

        <Separator />

        <!-- Folder + Project row -->
        <div class="grid grid-cols-2 gap-4">
          <div class="grid gap-2">
            <Label>{{ $t('fields.folder') }}</Label>
            <Select v-model="folder">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="f in createFolderOptions" :key="f" :value="f">
                  {{ $t('scopes.' + f) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label>{{ $t('fields.project') }}</Label>
            <Select
              :model-value="projectId || '__none__'"
              @update:model-value="projectId = $event === '__none__' ? '' : ($event as string)">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{{ $t('options.none') }}</SelectItem>
                <SelectItem v-for="project in projects" :key="project.project_id" :value="String(project.project_id)">
                  {{ project.title }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <!-- Organization: Delegated To -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.delegated_to') }}</Label>
          <template v-if="!isAddingNewContact">
            <Select :model-value="delegatedTo || '__none__'" @update:model-value="handleDelegatedToChange">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{{ $t('options.none') }}</SelectItem>
                <SelectItem v-for="contact in contacts" :key="contact" :value="contact">
                  {{ contact }}
                </SelectItem>
                <SelectItem value="__new__">{{ $t('options.new_contact') }}</SelectItem>
              </SelectContent>
            </Select>
          </template>
          <template v-else>
            <div class="flex gap-2">
              <Input
                v-model="newContactName"
                :placeholder="$t('fields.delegated_to')"
                class="flex-1"
                @keyup.enter="handleNewContact" />
              <Button variant="outline" size="sm" @click="handleNewContact">OK</Button>
              <Button variant="ghost" size="sm" @click="cancelNewContact">
                {{ $t('actions.cancel') }}
              </Button>
            </div>
          </template>
        </div>

        <!-- Scheduling: Due Date -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.due_date') }}</Label>
          <div class="flex gap-2">
            <Popover v-model:open="dueDatePopoverOpen">
              <PopoverTrigger as-child>
                <Button
                  variant="outline"
                  class="flex-1 justify-start text-left font-normal"
                  :class="{'text-muted-foreground': !dueDate}">
                  <CalendarDays class="mr-2 h-4 w-4" />
                  <span v-if="dueDate">{{ formatDate(dueDate) }}</span>
                  <span v-else>{{ $t('fields.due_date') }}</span>
                  <button
                    v-if="dueDate"
                    type="button"
                    class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                    @click.stop="dueDate = undefined">
                    <X class="h-3 w-3" />
                  </button>
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0" align="start">
                <Calendar :model-value="dueDate" locale="ru" :week-starts-on="1" @update:model-value="selectDueDate" />
              </PopoverContent>
            </Popover>
            <Input v-if="dueDate" v-model="dueTime" type="time" class="w-[7rem]" />
          </div>
        </div>

        <div v-if="canConfigureAutoComplete" class="flex items-center justify-between gap-4 rounded-lg border p-3">
          <Label for="create-auto-complete">{{ $t('fields.auto_complete') }}</Label>
          <Switch id="create-auto-complete" v-model="autoComplete" />
        </div>

        <!-- Scheduling: Reminders (only when due date is set) -->
        <div v-if="dueDate" class="grid gap-2">
          <Label>{{ $t('fields.reminders') }}</Label>
          <div v-if="reminders.length" class="flex flex-wrap gap-1">
            <Badge v-for="(reminder, idx) in reminders" :key="idx" variant="secondary" class="gap-1">
              {{ $t(getReminderLabel(reminder)) }}
              <button class="ml-0.5 rounded-sm hover:bg-muted" @click="removeReminder(idx)">
                <X class="h-3 w-3" />
              </button>
            </Badge>
          </div>
          <Select :model-value="'__placeholder__'" @update:model-value="handleReminderSelect">
            <SelectTrigger>
              <SelectValue :placeholder="$t('actions.add_reminder')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="preset in reminderPresets"
                :key="preset.value"
                :value="preset.value"
                :disabled="reminders.includes(preset.value)">
                {{ $t(preset.label) }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Scheduling: Recurring -->
        <Collapsible>
          <CollapsibleTrigger as-child>
            <Button variant="ghost" class="w-full justify-between px-0 font-normal">
              {{ $t('fields.recurring') }}
              <ChevronDown class="h-4 w-4" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div class="grid gap-3 pt-2">
              <div class="grid gap-2">
                <Label for="create-schedule">{{ $t('fields.schedule') }}</Label>
                <Input id="create-schedule" v-model="recurringSchedule" placeholder="e.g. 'every Monday at 10:00'" />
              </div>
              <div class="grid gap-2">
                <Label for="create-condition">{{ $t('fields.condition') }}</Label>
                <Input id="create-condition" v-model="recurringCondition" placeholder="e.g. '+10 days'" />
              </div>
              <div class="grid gap-2">
                <Label>{{ $t('fields.start_date') }}</Label>
                <Popover v-model:open="recurringStartPopoverOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      class="w-full justify-start text-left font-normal"
                      :class="{'text-muted-foreground': !recurringStartAt}">
                      <CalendarDays class="mr-2 h-4 w-4" />
                      <span v-if="recurringStartAt">{{ formatDate(recurringStartAt) }}</span>
                      <span v-else>{{ $t('fields.start_date') }}</span>
                      <button
                        v-if="recurringStartAt"
                        type="button"
                        class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                        @click.stop="recurringStartAt = undefined">
                        <X class="h-3 w-3" />
                      </button>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-auto p-0" align="start">
                    <Calendar
                      :model-value="recurringStartAt"
                      locale="ru"
                      :week-starts-on="1"
                      @update:model-value="selectRecurringStartAt" />
                  </PopoverContent>
                </Popover>
              </div>
              <div class="grid gap-2">
                <Label>{{ $t('fields.end_date') }}</Label>
                <Popover v-model:open="recurringEndPopoverOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      class="w-full justify-start text-left font-normal"
                      :class="{'text-muted-foreground': !recurringEndAt}">
                      <CalendarDays class="mr-2 h-4 w-4" />
                      <span v-if="recurringEndAt">{{ formatDate(recurringEndAt) }}</span>
                      <span v-else>{{ $t('fields.end_date') }}</span>
                      <button
                        v-if="recurringEndAt"
                        type="button"
                        class="ml-auto rounded-sm opacity-50 hover:opacity-100"
                        @click.stop="recurringEndAt = undefined">
                        <X class="h-3 w-3" />
                      </button>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-auto p-0" align="start">
                    <Calendar
                      :model-value="recurringEndAt"
                      locale="ru"
                      :week-starts-on="1"
                      @update:model-value="selectRecurringEndAt" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        <!-- Duration + Energy row -->
        <div class="grid grid-cols-2 gap-4">
          <div class="grid gap-2">
            <Label>{{ $t('fields.duration') }}</Label>
            <Select
              :model-value="duration || '__none__'"
              @update:model-value="duration = $event === '__none__' ? '' : ($event as string)">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="opt in durationOptions"
                  :key="opt.value || '__none__'"
                  :value="opt.value || '__none__'">
                  {{ $t(opt.label) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-2">
            <Label>{{ $t('fields.energy') }}</Label>
            <Select
              :model-value="energy || '__none__'"
              @update:model-value="energy = $event === '__none__' ? '' : ($event as string)">
              <SelectTrigger>
                <SelectValue :placeholder="$t('options.none')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="opt in energyOptions"
                  :key="opt.value || '__none__'"
                  :value="opt.value || '__none__'">
                  {{ $t(opt.label) }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <!-- Organization: Tags -->
        <div class="grid gap-2">
          <Label>{{ $t('fields.tags') }}</Label>
          <TagsMultiSelect v-model="selectedTags" :tags="tags" />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="handleClose">{{ $t('actions.cancel') }}</Button>
        <Button :disabled="!canSave" @click="handleSave">{{ $t('actions.save') }}</Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <!-- Discard confirmation -->
  <AlertDialog :open="discardDialogOpen" @update:open="discardDialogOpen = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('messages.unsaved_changes') }}</AlertDialogTitle>
        <AlertDialogDescription>{{ $t('messages.unsaved_changes_confirm') }}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{{ $t('actions.continue_editing') }}</AlertDialogCancel>
        <AlertDialogAction @click="discardAndClose">{{ $t('actions.discard') }}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
